import React, { useState, useEffect } from "react";
import {
    MdEvent, MdCheckCircle, MdCancel, MdStar, MdAccessTime,
    MdWarning, MdClose, MdPerson, MdKeyboardArrowLeft, MdKeyboardArrowRight, MdHistory
} from "react-icons/md";

import { appointmentsAPI, ratingsAPI } from "services/api";
import { useAuth } from "context/AuthContext";

// --- YARDIMCI FONKSİYONLAR ---
// İstenen Format: 18 Nisan 2026 Çarşamba
const formatLongDate = (dateString) => {
    if (!dateString) return "Geçersiz Tarih";
    try {
        const dateObj = new Date(dateString);
        return new Intl.DateTimeFormat('tr-TR', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
            weekday: 'long'
        }).format(dateObj);
    } catch {
        return dateString.split("T")[0];
    }
};

const formatTime = (timeString) => {
    if (!timeString || typeof timeString !== 'string') return "";
    return timeString.substring(0, 5);
};

// --- İNTERAKTİF YILDIZ BİLEŞENİ ---
const StarRatingInput = ({ rating, setRating }) => {
    const [hoverRating, setHoverRating] = useState(0);
    return (
        <div className="flex justify-center gap-2 my-4">
            {[1, 2, 3, 4, 5].map((star) => (
                <MdStar
                    key={star}
                    className={`text-4xl cursor-pointer transition-colors duration-200 ${
                        (hoverRating || rating) >= star ? "text-amber-400 drop-shadow-md" : "text-gray-300 dark:text-gray-600"
                    }`}
                    onClick={() => setRating(star)}
                    onMouseEnter={() => setHoverRating(star)}
                    onMouseLeave={() => setHoverRating(0)}
                />
            ))}
        </div>
    );
};

// --- SKELETON LOADERS ---
const AppoSkeleton = () => (
    <div className="flex flex-col gap-4 animate-pulse">
        {[...Array(4)].map((_, i) => (
            <div key={i} className="h-28 w-full rounded-[20px] bg-gray-100 dark:bg-navy-900/50" />
        ))}
    </div>
);

// Sabit
const PAGE_SIZE_PAST = 5;

const CustomerAppointmentHistory = () => {
    // --- STATELER (WORK-LOGIC BİREBİR) ---
    const [appointments, setAppointments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Değerlendirme Modal Stateleri
    const [isReviewModalVisible, setIsReviewModalVisible] = useState(false);
    const [reviewingAppointment, setReviewingAppointment] = useState(null);
    const [rating, setRating] = useState(0);
    const [comment, setComment] = useState("");
    const [reviewSuccess, setReviewSuccess] = useState(false);
    const [reviewError, setReviewError] = useState(null);
    const [isReviewing, setIsReviewing] = useState(false); // Buton Loader

    // İptal Modal Stateleri
    const [isCancelModalVisible, setIsCancelModalVisible] = useState(false);
    const [cancellingAppointment, setCancellingAppointment] = useState(null);
    const [cancelError, setCancelError] = useState(null);
    const [isCancelling, setIsCancelling] = useState(false); // Buton Loader

    // Geçmiş Sayfalama
    const [pastCurrentPage, setPastCurrentPage] = useState(1);

    const { logout } = useAuth();
    const handleAuthError = () => logout();

    // --- VERİ ÇEKME ---
    useEffect(() => {
        setLoading(true);
        setError(null);
        appointmentsAPI
            .getMyAppointments()
            .then((response) => {
                if (!response || !response.appointments) {
                    throw new Error("API'den geçerli randevu verisi alınamadı.");
                }
                const mappedAppointments = response.appointments.map((app) => ({
                    id: app.id,
                    service: app.service?.name || "Bilinmeyen Hizmet",
                    barberName: app.staff ? `${app.staff.name} ${app.staff.surname}` : "Bilinmeyen Personel",
                    date: formatLongDate(app.appointment_date),
                    time: formatTime(app.appointment_time),
                    status: mapStatus(app.status),
                    reviewed: app.rating !== null,
                    rawStatus: app.status,
                    appointmentDateISO: app.appointment_date,
                    rawTime: app.appointment_time,
                }))
                    // Tarih/Saate göre en yeni en üstte
                    .sort((a, b) => {
                        const dateA = new Date(`${a.appointmentDateISO.split('T')[0]}T${a.rawTime}`);
                        const dateB = new Date(`${b.appointmentDateISO.split('T')[0]}T${b.rawTime}`);
                        return dateB - dateA;
                    });

                setAppointments(mappedAppointments);
            })
            .catch((error) => {
                console.error("Randevular yüklenirken hata:", error);
                if (String(error.message).includes('401') || String(error.message).includes('Unauthorized')) {
                    handleAuthError();
                    return;
                }
                setError(error.message || "Randevular yüklenirken bir hata oluştu.");
            })
            .finally(() => {
                setLoading(false);
            });
    }, []);

    // --- DURUM & KURAL FONKSİYONLARI ---
    const mapStatus = (backendStatus) => {
        switch (backendStatus) {
            case "pending": return "Bekleniyor";
            case "approved": return "Onaylandı";
            case "completed": return "Tamamlandı";
            case "cancelled": return "İptal Edildi";
            default: return "Bilinmeyen";
        }
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case "Bekleniyor": return { color: "text-amber-500", bg: "bg-amber-50 dark:bg-amber-500/10", icon: <MdAccessTime /> };
            case "Onaylandı": return { color: "text-blue-500", bg: "bg-blue-50 dark:bg-blue-500/10", icon: <MdEvent /> };
            case "Tamamlandı": return { color: "text-green-500", bg: "bg-green-50 dark:bg-green-500/10", icon: <MdCheckCircle /> };
            case "İptal Edildi": return { color: "text-red-500", bg: "bg-red-50 dark:bg-red-500/10", icon: <MdCancel /> };
            default: return { color: "text-gray-500", bg: "bg-gray-100 dark:bg-navy-900", icon: <MdEvent /> };
        }
    };

    const canReview = (appointment) => {
        const isCompleted = appointment.rawStatus === "completed";
        const notReviewed = !appointment.reviewed;
        if (!isCompleted || !notReviewed) return false;
        try {
            const today = new Date(); today.setHours(0, 0, 0, 0);
            const appointmentDate = new Date(appointment.appointmentDateISO); appointmentDate.setHours(0, 0, 0, 0);
            const diffInDays = Math.floor((today.getTime() - appointmentDate.getTime()) / (1000 * 60 * 60 * 24));
            return diffInDays >= 0 && diffInDays <= 7; // 7 gün kuralı
        } catch (error) {
            return false;
        }
    };

    // --- İPTAL MODAL İŞLEMLERİ ---
    const handleOpenCancelModal = (appointment) => {
        setCancellingAppointment(appointment);
        setCancelError(null);
        setIsCancelModalVisible(true);
    };

    const handleCloseCancelModal = () => {
        if (isCancelling) return;
        setIsCancelModalVisible(false);
        setCancellingAppointment(null);
        setCancelError(null);
    };

    const handleCancelAppointment = () => {
        if (!cancellingAppointment) return;
        setCancelError(null);
        setIsCancelling(true);

        appointmentsAPI
            .updateStatus(cancellingAppointment.id, 'cancelled')
            .then(() => {
                setAppointments(
                    appointments.map((app) =>
                        app.id === cancellingAppointment.id ? { ...app, status: "İptal Edildi", rawStatus: "cancelled" } : app
                    )
                );
                handleCloseCancelModal();
            })
            .catch((error) => {
                setCancelError(error.response?.data?.message || "Randevu iptal edilirken bir hata oluştu!");
            })
            .finally(() => setIsCancelling(false));
    };

    // --- DEĞERLENDİRME MODAL İŞLEMLERİ ---
    const handleOpenReviewModal = (appointment) => {
        setReviewingAppointment(appointment);
        setReviewError(null);
        setIsReviewModalVisible(true);
    };

    const handleCloseReviewModal = () => {
        if (isReviewing) return;
        setIsReviewModalVisible(false);
        setReviewingAppointment(null);
        setRating(0);
        setComment("");
        setReviewError(null);
    };

    const handleSubmitReview = () => {
        if (!reviewingAppointment || rating === 0) {
            setReviewError("Lütfen en az 1 yıldız seçin.");
            return;
        }
        setReviewError(null);
        setIsReviewing(true);

        const reviewData = {
            appointment_id: reviewingAppointment.id,
            rating: rating,
            comment: comment || null,
        };

        ratingsAPI
            .create(reviewData)
            .then(() => {
                setAppointments(
                    appointments.map((app) => (app.id === reviewingAppointment.id ? { ...app, reviewed: true } : app))
                );
                setReviewSuccess(true);
                setTimeout(() => setReviewSuccess(false), 4000);
                handleCloseReviewModal();
            })
            .catch((error) => {
                setReviewError(error.response?.data?.message || "Değerlendirme gönderilemedi!");
            })
            .finally(() => setIsReviewing(false));
    };

    // --- FİLTRELEME & SAYFALAMA ---
    const upcomingAppointments = appointments.filter((a) => a.rawStatus === "pending" || a.rawStatus === "approved");
    const pastAppointments = appointments.filter((a) => a.rawStatus === "completed" || a.rawStatus === "cancelled");

    const pastTotalPages = Math.ceil(pastAppointments.length / PAGE_SIZE_PAST);
    const pastPageStartIndex = (pastCurrentPage - 1) * PAGE_SIZE_PAST;
    const currentPagePastItems = pastAppointments.slice(pastPageStartIndex, pastPageStartIndex + PAGE_SIZE_PAST);

    // --- RENDER ---
    return (
        <div className="mt-5 flex w-full flex-col gap-6 pb-20 animate-fadeIn">

            {/* 1. ÜST BİLGİ & BİLDİRİMLER */}
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 bg-white p-6 rounded-[20px] shadow-3xl shadow-shadow-500 dark:bg-navy-800 dark:shadow-none border border-gray-100 dark:border-white/10">
                <div className="flex items-center gap-4 w-full md:w-auto">
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-500/10 text-brand-500 dark:bg-brand-400/10 dark:text-brand-400 flex-none">
                        <MdEvent className="text-3xl" />
                    </div>
                    <div>
                        <h4 className="text-xl font-bold text-navy-700 dark:text-white">Randevularım</h4>
                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                            Yaklaşan randevularınızı iptal edebilir veya geçmiş hizmetleri değerlendirebilirsiniz.
                        </p>
                    </div>
                </div>
            </div>

            {error && (
                <div className="p-4 rounded-xl bg-red-50 text-red-600 border border-red-200 dark:bg-red-500/10 dark:border-red-500/20 dark:text-red-400 text-sm font-bold flex items-center gap-2">
                    <MdWarning className="text-xl" /> {error}
                </div>
            )}

            {reviewSuccess && (
                <div className="p-4 rounded-xl bg-green-50 text-green-600 border border-green-200 dark:bg-green-500/10 dark:border-green-500/20 dark:text-green-400 text-sm font-bold flex items-center gap-2 animate-fadeIn">
                    <MdCheckCircle className="text-xl" /> Değerlendirmeniz için teşekkür ederiz!
                </div>
            )}

            {/* 2. GRID YAPISI: GELECEK VE GEÇMİŞ RANDEVULAR */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                {/* 2A. GELECEK RANDEVULARIM */}
                <div className="flex flex-col bg-white dark:bg-navy-800 rounded-[20px] shadow-sm border border-gray-100 dark:border-white/10 overflow-hidden h-full">
                    <div className="bg-gradient-to-r from-brand-500 to-brand-600 p-5">
                        <h5 className="text-lg font-bold text-white">Gelecek Randevularım ({upcomingAppointments.length})</h5>
                    </div>

                    <div className="p-5 flex-1 flex flex-col gap-4">
                        {loading ? <AppoSkeleton /> : upcomingAppointments.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-10 opacity-50 m-auto">
                                <MdEvent className="text-5xl text-gray-400 mb-2" />
                                <p className="font-bold text-navy-700 dark:text-white">Yaklaşan randevunuz bulunmuyor.</p>
                            </div>
                        ) : (
                            upcomingAppointments.map((app) => {
                                const { color, bg, icon } = getStatusBadge(app.status);
                                return (
                                    <div key={app.id} className="flex flex-col p-4 rounded-2xl border border-gray-100 dark:border-white/5 bg-gray-50/50 dark:bg-navy-900/30">
                                        <div className="flex justify-between items-start mb-3">
                                            <div>
                                                <h6 className="text-base font-bold text-navy-700 dark:text-white">{app.service}</h6>
                                                <div className="flex items-center gap-1 mt-1 text-xs font-bold text-gray-500 dark:text-gray-400">
                                                    <MdPerson className="text-brand-500" /> {app.barberName}
                                                </div>
                                            </div>
                                            <span className={`flex items-center gap-1 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest ${bg} ${color}`}>
                        {icon} {app.status}
                      </span>
                                        </div>

                                        {/* VURGULU TARİH VE SAAT ALANI */}
                                        <div className="flex items-center gap-2 mb-4 bg-white dark:bg-navy-800 p-3 rounded-xl border border-gray-100 dark:border-white/5">
                                            <div className="h-10 w-10 flex-none rounded-full bg-brand-50 dark:bg-brand-400/10 flex items-center justify-center text-brand-500 text-xl">
                                                <MdAccessTime />
                                            </div>
                                            <div className="flex flex-wrap items-center gap-2">
                                                <span className="text-sm font-bold text-navy-700 dark:text-white">{app.date}</span>
                                                <span className="text-xs font-black bg-brand-500 text-white px-2 py-0.5 rounded-md shadow-sm">
                          {app.time}
                        </span>
                                            </div>
                                        </div>

                                        <button
                                            onClick={() => handleOpenCancelModal(app)}
                                            className="w-full py-2.5 rounded-xl border border-red-500 text-red-500 font-bold text-sm hover:bg-red-500 hover:text-white transition-all dark:border-red-400 dark:text-red-400 dark:hover:bg-red-500 dark:hover:text-white"
                                        >
                                            Randevuyu İptal Et
                                        </button>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>

                {/* 2B. GEÇMİŞ RANDEVULARIM */}
                <div className="flex flex-col bg-white dark:bg-navy-800 rounded-[20px] shadow-sm border border-gray-100 dark:border-white/10 overflow-hidden h-full">
                    <div className="bg-gradient-to-r from-navy-700 to-navy-800 dark:from-navy-900 dark:to-black p-5">
                        <h5 className="text-lg font-bold text-white">Geçmiş Randevularım ({pastAppointments.length})</h5>
                    </div>

                    <div className="p-5 flex-1 flex flex-col gap-4">
                        {loading ? <AppoSkeleton /> : pastAppointments.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-10 opacity-50 m-auto">
                                <MdHistory className="text-5xl text-gray-400 mb-2" />
                                <p className="font-bold text-navy-700 dark:text-white">Geçmiş randevunuz bulunmuyor.</p>
                            </div>
                        ) : (
                            <>
                                <div className="flex flex-col gap-4 flex-1">
                                    {currentPagePastItems.map(app => {
                                        const { color, bg, icon } = getStatusBadge(app.status);
                                        const showReviewBtn = canReview(app);

                                        return (
                                            <div key={app.id} className="flex flex-col p-4 rounded-2xl border border-gray-100 dark:border-white/5 bg-gray-50/50 dark:bg-navy-900/30">
                                                <div className="flex justify-between items-start mb-3">
                                                    <div>
                                                        <h6 className="text-sm font-bold text-navy-700 dark:text-white">{app.service}</h6>
                                                        <span className="text-xs font-bold text-gray-500 flex items-center gap-1 mt-0.5">
                               {app.barberName}
                            </span>
                                                    </div>
                                                    <span className={`flex items-center gap-1 px-2 py-1 rounded-full text-[9px] font-bold uppercase tracking-widest ${bg} ${color}`}>
                            {icon} {app.status}
                          </span>
                                                </div>

                                                {/* VURGULU TARİH ALANI (KÜÇÜK) */}
                                                <div className="flex flex-wrap items-center gap-2 mb-3">
                                                    <span className="text-xs font-bold text-navy-700 dark:text-gray-300">{app.date}</span>
                                                    <span className="text-[10px] font-black bg-gray-200 dark:bg-navy-700 text-navy-700 dark:text-white px-2 py-0.5 rounded-md">
                            {app.time}
                          </span>
                                                </div>

                                                {showReviewBtn ? (
                                                    <button
                                                        onClick={() => handleOpenReviewModal(app)}
                                                        className="w-full py-2 rounded-xl bg-amber-500 text-white font-bold text-sm shadow-md shadow-amber-500/30 hover:bg-amber-600 transition-all flex items-center justify-center gap-2"
                                                    >
                                                        <MdStar className="text-lg" /> Değerlendir
                                                    </button>
                                                ) : (app.rawStatus === 'completed' && app.reviewed) ? (
                                                    <div className="w-full py-2 rounded-xl bg-gray-100 dark:bg-navy-900 text-green-500 font-bold text-xs flex items-center justify-center gap-1">
                                                        <MdCheckCircle className="text-sm" /> Değerlendirildi
                                                    </div>
                                                ) : null}
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* GEÇMİŞ SAYFALAMA */}
                                {pastTotalPages > 1 && (
                                    <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100 dark:border-white/10">
                                        <button
                                            onClick={() => setPastCurrentPage(p => Math.max(1, p - 1))}
                                            disabled={pastCurrentPage === 1}
                                            className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 disabled:opacity-40 hover:bg-gray-50 dark:border-white/10 dark:bg-navy-800 dark:hover:bg-navy-700 transition-all"
                                        >
                                            <MdKeyboardArrowLeft className="text-lg" />
                                        </button>
                                        <span className="text-xs font-bold text-gray-500">Sayfa {pastCurrentPage} / {pastTotalPages}</span>
                                        <button
                                            onClick={() => setPastCurrentPage(p => Math.min(pastTotalPages, p + 1))}
                                            disabled={pastCurrentPage === pastTotalPages}
                                            className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 disabled:opacity-40 hover:bg-gray-50 dark:border-white/10 dark:bg-navy-800 dark:hover:bg-navy-700 transition-all"
                                        >
                                            <MdKeyboardArrowRight className="text-lg" />
                                        </button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>

            </div>

            {/* --- MODALLAR --- */}

            {/* 3. İPTAL ONAY MODALI */}
            {isCancelModalVisible && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-navy-900/60 backdrop-blur-sm p-4 transition-all animate-fadeIn">
                    <div className="relative w-full max-w-md bg-white dark:bg-navy-800 rounded-[24px] shadow-2xl p-6">
                        <div className="flex items-center gap-4 mb-4">
                            <div className="h-12 w-12 rounded-full bg-red-50 text-red-500 flex items-center justify-center dark:bg-red-500/10 dark:text-red-400">
                                <MdWarning className="text-2xl" />
                            </div>
                            <h3 className="text-xl font-bold text-navy-700 dark:text-white">Randevuyu İptal Et</h3>
                        </div>

                        {cancelError && (
                            <div className="mb-4 p-3 rounded-xl bg-red-50 text-sm font-bold text-red-600 dark:bg-red-500/10 dark:text-red-400">
                                {cancelError}
                            </div>
                        )}

                        <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                            <strong className="text-navy-700 dark:text-white">{cancellingAppointment?.date}</strong> tarihindeki
                            <strong className="text-navy-700 dark:text-white"> {cancellingAppointment?.service}</strong> randevunuzu iptal etmek istediğinize emin misiniz?
                        </p>
                        <p className="text-xs font-bold text-red-500 mb-6">Bu işlem geri alınamaz.</p>

                        <div className="flex justify-end gap-3">
                            <button onClick={handleCloseCancelModal} disabled={isCancelling} className="px-6 h-11 rounded-xl text-sm font-bold text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-navy-700 transition-all">Vazgeç</button>
                            <button onClick={handleCancelAppointment} disabled={isCancelling} className="flex items-center justify-center h-11 px-8 rounded-xl bg-red-500 text-sm font-bold text-white shadow-lg shadow-red-500/30 hover:bg-red-600 disabled:opacity-60 transition-all active:scale-95">
                                {isCancelling ? <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : 'Evet, İptal Et'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* 4. DEĞERLENDİRME MODALI */}
            {isReviewModalVisible && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-navy-900/60 backdrop-blur-sm p-4 transition-all animate-fadeIn">
                    <div className="relative w-full max-w-md bg-white dark:bg-navy-800 rounded-[24px] shadow-2xl p-6 text-center">

                        <button onClick={handleCloseReviewModal} disabled={isReviewing} className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-white transition-colors">
                            <MdClose className="text-xl" />
                        </button>

                        <div className="mx-auto h-16 w-16 rounded-full bg-amber-50 text-amber-500 flex items-center justify-center dark:bg-amber-500/10 dark:text-amber-400 mb-4">
                            <MdStar className="text-4xl drop-shadow-sm" />
                        </div>

                        <h3 className="text-xl font-bold text-navy-700 dark:text-white mb-2">Hizmeti Değerlendirin</h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                            <strong className="text-navy-700 dark:text-white">{reviewingAppointment?.barberName}</strong> tarafından verilen <strong className="text-navy-700 dark:text-white">{reviewingAppointment?.service}</strong> hizmetine kaç puan verirsiniz? (Anonim)
                        </p>

                        {reviewError && (
                            <div className="my-3 p-3 rounded-xl bg-red-50 text-sm font-bold text-red-600 dark:bg-red-500/10 dark:text-red-400 text-left">
                                {reviewError}
                            </div>
                        )}

                        <StarRatingInput rating={rating} setRating={setRating} />

                        <textarea
                            placeholder="Yorumunuzu buraya yazın... (Opsiyonel)"
                            rows="3"
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            className="w-full resize-none rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm font-medium text-navy-700 outline-none focus:border-amber-500 dark:border-white/10 dark:bg-navy-900 dark:text-white transition-all mb-6"
                        />

                        <button
                            onClick={handleSubmitReview}
                            disabled={rating === 0 || isReviewing}
                            className="flex w-full items-center justify-center h-12 rounded-xl bg-brand-500 text-sm font-bold text-white shadow-lg shadow-brand-500/30 hover:bg-brand-600 disabled:opacity-50 transition-all active:scale-95"
                        >
                            {isReviewing ? <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : 'Değerlendirmeyi Gönder'}
                        </button>
                    </div>
                </div>
            )}

        </div>
    );
};

export default CustomerAppointmentHistory;